/**
 * framed-upgrade.js
 *
 * Inline "Upgrade to Framed" prompt for the product page.
 *
 * IMPORTANT — this file does NOT reimplement variant switching. It locates the
 * real <variant-selects> control for the matching "Framed" option value and
 * fires a native `change` event on it. The theme's own product-info.js /
 * variant-selects.js (global.js) handle everything downstream: price,
 * compare-at price, media, inventory, URL, pickup availability, and the
 * hidden variant id input that Add to Cart / Buy Now submit.
 *
 * This file only ever:
 *   1. Reads product/variant data that Liquid already embedded (read-only).
 *   2. Decides whether to show/hide the box.
 *   3. On click, flips one existing form control, dispatches `change`, and
 *      immediately syncs the visual active state on option labels/buttons.
 */

(function () {
  'use strict';

  if (customElements.get('framed-upgrade-box')) return;

  function pubSubAvailable() {
    return (
      typeof window.subscribe === 'function' &&
      typeof window.publish === 'function' &&
      typeof window.PUB_SUB_EVENTS === 'object' &&
      window.PUB_SUB_EVENTS !== null
    );
  }

  var UNFRAMED_VALUE_PATTERN =
    /\bunframed\b|\bprint\s*only\b|\bno\s*frame\b|\bwithout\s*frame\b|\bposter\s*only\b/i;
  var FRAME_NAME_PATTERN = /\bfram(e|ing)\b/i;
  var SIZE_NAME_PATTERN = /\bsize\b/i;

  function arraysEqual(a, b) {
    if (a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  function safeParseJSON(text, fallback) {
    try {
      return JSON.parse(text);
    } catch (error) {
      console.error('[framed-upgrade] Failed to parse embedded JSON', error);
      return fallback;
    }
  }

  class FramedUpgradeBoxElement extends HTMLElement {
    constructor() {
      super();
      this.variants = [];
      this.options = [];
      this.upgradeAxis = null;
      this.sizeAxisPosition = null;
      this.pendingFramedVariant = null;
      this.currentVariantSnapshot = null;
      this.variantChangeUnsubscriber = undefined;
      this._onButtonClick = this.onUpgradeClick.bind(this);
      this._onFormChange = this.onFormChange.bind(this);
    }

    connectedCallback() {
      this.cacheElements();
      if (!this.variants.length || !this.options.length) return;

      this.upgradeAxis = this.identifyUpgradeOption();
      if (!this.upgradeAxis) {
        if (window.console && console.warn) {
          console.warn(
            '[framed-upgrade] No framing axis detected for section',
            this.sectionId,
            '— this product may use a third configuration pattern not yet supported.'
          );
        }
        return;
      }

      this.sizeAxisPosition = this.identifySizeOption();
      this.bindEvents();
      this.listenForVariantChanges();
      this.evaluate(this.getCurrentVariant());
    }

    disconnectedCallback() {
      this.destroy();
    }

    destroy() {
      if (this.upgradeButton) {
        this.upgradeButton.removeEventListener('click', this._onButtonClick);
      }
      document.removeEventListener('change', this._onFormChange, true);
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
        this.variantChangeUnsubscriber = undefined;
      }
    }

    // -------------------------------------------------------------------------
    // Setup
    // -------------------------------------------------------------------------

    cacheElements() {
      this.sectionId = this.dataset.sectionId;
      this.productFormId = this.dataset.productFormId;
      this.currency =
        this.dataset.currency ||
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
        undefined;

      this.upgradeButton = this.querySelector('[data-framed-upgrade-button]');
      this.buttonLabel = this.querySelector('[data-framed-upgrade-button-label]');
      this.priceDiffEl = this.querySelector('[data-framed-upgrade-price-diff]');
      this.sizeEl = this.querySelector('[data-framed-upgrade-size]');

      var variantsScript = document.querySelector(
        'script[data-framed-upgrade-variants="' + this.sectionId + '"]'
      );
      var optionsScript = document.querySelector(
        'script[data-framed-upgrade-options="' + this.sectionId + '"]'
      );
      var valueIdsScript = document.querySelector(
        'script[data-framed-upgrade-option-value-ids="' + this.sectionId + '"]'
      );

      this.variants = variantsScript ? safeParseJSON(variantsScript.textContent, []) : [];
      this.options = optionsScript ? safeParseJSON(optionsScript.textContent, []) : [];
      this.optionValueIds = valueIdsScript ? safeParseJSON(valueIdsScript.textContent, {}) : {};
    }

    bindEvents() {
      if (this.upgradeButton) {
        this.upgradeButton.addEventListener('click', this._onButtonClick);
      }
      document.addEventListener('change', this._onFormChange, true);
    }

    onFormChange(event) {
      var form = document.getElementById(this.productFormId);
      if (!form || !form.contains(event.target)) return;
      var self = this;
      window.setTimeout(function () {
        self.evaluate(self.getCurrentVariant());
      }, 0);
    }

    listenForVariantChanges() {
      if (!pubSubAvailable()) return;
      var self = this;
      this.variantChangeUnsubscriber = window.subscribe(
        window.PUB_SUB_EVENTS.variantChange,
        function (event) {
          if (!event || !event.data || event.data.sectionId !== self.sectionId) return;
          self.evaluate(event.data.variant);
        }
      );
    }

    // -------------------------------------------------------------------------
    // Matching
    // -------------------------------------------------------------------------

    collectValuesAtPosition(pos) {
      var seen = {};
      var values = [];
      for (var i = 0; i < this.variants.length; i++) {
        var value = this.variants[i].options[pos];
        if (value == null || seen[value]) continue;
        seen[value] = true;
        values.push(value);
      }
      return values;
    }

    identifyUpgradeOption() {
      var candidates = [];

      for (var pos = 0; pos < this.options.length; pos++) {
        var optionMeta = this.options[pos];
        var valuesAtPos = this.collectValuesAtPosition(pos);
        var nonFramedValues = valuesAtPos.filter(function (v) {
          return UNFRAMED_VALUE_PATTERN.test(v);
        });
        var framedValues = valuesAtPos.filter(function (v) {
          return !UNFRAMED_VALUE_PATTERN.test(v);
        });

        if (nonFramedValues.length >= 1 && framedValues.length >= 1) {
          candidates.push({
            position: pos,
            optionName: optionMeta ? optionMeta.name : null,
            optionPosition: optionMeta ? optionMeta.position : pos + 1,
            framedValues: this.orderFramedValues(framedValues),
            nonFramedValues: nonFramedValues,
            nameMatches: !!(optionMeta && FRAME_NAME_PATTERN.test(optionMeta.name || '')),
          });
        }
      }

      if (!candidates.length) return null;

      var named = candidates.filter(function (c) { return c.nameMatches; });
      return named[0] || candidates[0];
    }

    orderFramedValues(values) {
      var black = values.filter(function (v) { return /^black$/i.test(v); });
      var rest = values.filter(function (v) { return !/^black$/i.test(v); });
      return black.concat(rest);
    }

    identifySizeOption() {
      for (var pos = 0; pos < this.options.length; pos++) {
        var optionMeta = this.options[pos];
        if (optionMeta && SIZE_NAME_PATTERN.test(optionMeta.name || '')) return pos;
      }
      return null;
    }

    isPrintOnly(variant) {
      if (!variant || !this.upgradeAxis) return false;
      var value = variant.options[this.upgradeAxis.position];
      return value != null && this.upgradeAxis.nonFramedValues.indexOf(value) !== -1;
    }

    findMatchingFramedVariant(variant) {
      if (!variant || !this.upgradeAxis) return null;

      var framedValues = this.upgradeAxis.framedValues;
      var fallbackUnavailable = null;

      for (var f = 0; f < framedValues.length; f++) {
        var targetOptions = variant.options.slice();
        targetOptions[this.upgradeAxis.position] = framedValues[f];

        for (var i = 0; i < this.variants.length; i++) {
          var candidate = this.variants[i];
          if (!arraysEqual(candidate.options, targetOptions)) continue;

          var diffCount = 0;
          for (var p = 0; p < candidate.options.length; p++) {
            if (candidate.options[p] !== variant.options[p]) diffCount++;
          }
          if (diffCount !== 1) continue;

          if (candidate.available) return candidate;
          if (!fallbackUnavailable) fallbackUnavailable = candidate;
          break;
        }
      }

      return fallbackUnavailable;
    }

    calculateDifference(currentVariant, framedVariant) {
      var framedPrice = Number(framedVariant.price);
      var currentPrice = Number(currentVariant.price);
      if (isNaN(framedPrice) || isNaN(currentPrice)) return null;
      return framedPrice - currentPrice;
    }

    // -------------------------------------------------------------------------
    // Reading current state
    // -------------------------------------------------------------------------

    getSelectedVariantId() {
      var form = document.getElementById(this.productFormId);
      var input = form && form.querySelector('input[name="id"]');
      return input ? Number(input.value) : null;
    }

    getVariantById(id) {
      if (id == null) return null;
      for (var i = 0; i < this.variants.length; i++) {
        if (this.variants[i].id === id) return this.variants[i];
      }
      return null;
    }

    getCurrentVariant() {
      return this.getVariantById(this.getSelectedVariantId());
    }

    // -------------------------------------------------------------------------
    // Show / hide
    // -------------------------------------------------------------------------

    evaluate(variant) {
      variant = variant || this.getCurrentVariant();

      if (!variant || !this.isPrintOnly(variant)) {
        this.hideUpgrade();
        return;
      }

      var framedVariant = this.findMatchingFramedVariant(variant);
      if (!framedVariant || !framedVariant.available) {
        this.hideUpgrade();
        return;
      }

      var diff = this.calculateDifference(variant, framedVariant);
      if (diff == null) {
        this.hideUpgrade();
        return;
      }

      this.showUpgrade(variant, framedVariant, diff);
    }

    showUpgrade(currentVariant, framedVariant, diff) {
      this.pendingFramedVariant = framedVariant;
      this.currentVariantSnapshot = currentVariant;

      if (this.priceDiffEl) {
        this.priceDiffEl.textContent = this.formatPriceDifference(diff);
      }

      if (this.sizeEl) {
        var sizeValue =
          this.sizeAxisPosition != null ? framedVariant.options[this.sizeAxisPosition] : null;
        var sizeText = sizeValue || framedVariant.title || '';
        this.sizeEl.textContent = sizeText;
        this.sizeEl.hidden = !sizeText;
      }

      this.hidden = false;
      this.removeAttribute('hidden');
    }

    hideUpgrade() {
      this.pendingFramedVariant = null;
      this.currentVariantSnapshot = null;
      this.setAttribute('hidden', '');
      this.hidden = true;
    }

    formatPriceDifference(cents) {
      var amount = Math.abs(cents) / 100;
      var sign = cents > 0 ? '+' : cents < 0 ? '\u2212' : '';

      if (this.currency) {
        try {
          var formatted = new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency: this.currency,
          }).format(amount);
          return sign + formatted;
        } catch (error) {
          // fall through
        }
      }

      return sign + amount.toFixed(2) + (this.currency ? ' ' + this.currency : '');
    }

    // -------------------------------------------------------------------------
    // Switching the variant
    // -------------------------------------------------------------------------

    onUpgradeClick(event) {
  event.preventDefault();

  if (
    !this.pendingFramedVariant ||
    this.upgradeButton.getAttribute('aria-disabled') === 'true'
  ) {
    return;
  }

  var framedVariant = this.pendingFramedVariant;
  var currentVariant = this.currentVariantSnapshot;
  var self = this;

  this.upgradeButton.setAttribute('aria-disabled', 'true');
  this.upgradeButton.classList.add('loading');

  var dispatched = false;

  try {
    dispatched = this.switchVariant(framedVariant);
  } catch (error) {
    console.error('[framed-upgrade] switchVariant error', error);
  }

  window.setTimeout(function () {
    var selectedVariantId = self.getSelectedVariantId();

    var urlVariantId = null;
    try {
      urlVariantId = Number(new URL(window.location.href).searchParams.get('variant'));
    } catch (e) {}

    var applied =
      dispatched &&
      (
        selectedVariantId === framedVariant.id ||
        urlVariantId === framedVariant.id
      );

    self.upgradeButton.removeAttribute('aria-disabled');
    self.upgradeButton.classList.remove('loading');

    console.log('[framed-upgrade] tracking check:', {
      dispatched: dispatched,
      selectedVariantId: selectedVariantId,
      urlVariantId: urlVariantId,
      expectedFramedVariantId: framedVariant.id,
      applied: applied
    });

    if (applied) {
      self.fireAnalytics(currentVariant, framedVariant);
      self.hideUpgrade();
    } else {
      console.error('[framed-upgrade] Variant switch did not confirm, tracking not fired.');
    }
  }, 700);
}

    /**
     * Locates the real <variant-selects> control(s), mutates them, dispatches
     * a native `change` event, then immediately syncs the visual active state
     * on option labels/buttons so the UI reflects the new selection without
     * waiting for the theme's async re-render cycle.
     */
    switchVariant(targetVariant) {
      var variantSelects = this.findVariantSelects();
      if (!variantSelects) {
        console.error('[framed-upgrade] variant-selects not found for section', this.sectionId);
        return false;
      }

      var currentVariant = this.getCurrentVariant();
      if (!currentVariant) return false;

      var lastMutatedControl = null;

      for (var pos = 0; pos < targetVariant.options.length; pos++) {
        if (currentVariant.options[pos] === targetVariant.options[pos]) continue;

        var optionMeta = this.options[pos];
        if (!optionMeta) continue;

        var targetValue = targetVariant.options[pos];
        var control = this.setOptionControlValue(variantSelects, optionMeta, targetValue);
        if (control) lastMutatedControl = control;
      }

      if (!lastMutatedControl) {
        console.error('[framed-upgrade] Could not locate a control to switch for the Framed variant.');
        return false;
      }

      this.clickOptionControl(lastMutatedControl);

      // Immediate visual sync for class-based themes.
      this.refreshOptionVisualState(variantSelects, targetVariant, currentVariant);

      // MutationObserver: the theme may fire an AJAX section re-render after
      // the change event, replacing the picker HTML with server-rendered markup.
      // If the server returns HTML with the old (Unframed) radio checked, the
      // pill reverts visually. The observer catches every such DOM replacement
      // and immediately re-asserts radio.checked = true on the Framed option.
      if (typeof this._watchForRerender === 'function') {
  this._watchForRerender(variantSelects, targetVariant, currentVariant);
}

      return true;
    }

    /**
     * Watches the variant-selects container for DOM replacements triggered by
     * the theme’s AJAX section re-render. After each replacement it calls
     * _assertRadioChecked() to immediately re-set the correct radio as checked,
     * ensuring the Framed pill stays visually selected.
     *
     * Auto-disconnects after 3 seconds (covers slow connections).
     */
    _watchForRerender(variantSelects, targetVariant, currentVariant) {
      var self = this;
      var _tv = targetVariant;
      var _cv = currentVariant;

      // Re-assert the checked state at multiple intervals.
      // Covers: immediate DOM state, theme JS that runs on next tick,
      // and AJAX re-renders that typically complete within 200–800 ms.
      [0, 200, 500, 1000].forEach(function (ms) {
        window.setTimeout(function () {
          self._assertAndRefresh(_tv, _cv);
        }, ms);
      });

      // MutationObserver on the broadest reliable scope: the product host
      // element (product-info, .product, or the section wrapper).
      // This catches element replacement at any nesting level.
      if (!window.MutationObserver) return;
      var host =
        self.closest('product-info') ||
        self.closest('.product') ||
        document.getElementById('MainProduct-' + self.sectionId) ||
        (variantSelects && variantSelects.closest('section'));
      if (!host) return;

      var timer = null;
      var observer = new MutationObserver(function () {
        if (timer) window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          self._assertAndRefresh(_tv, _cv);
        }, 0);
      });

      observer.observe(host, { childList: true, subtree: true });
      window.setTimeout(function () { observer.disconnect(); }, 3000);
    }

    /**
     * Finds the Framed radio anywhere inside the product host by its option
     * value string, sets it checked, and refreshes visual classes.
     * Works after AJAX re-renders that replace the entire picker element.
     */
    _assertAndRefresh(targetVariant, currentVariant) {
      if (!this.upgradeAxis) return;

      var pos = this.upgradeAxis.position;
      var targetValue = targetVariant.options[pos];
      var optionMeta = this.options[pos];

      // Search scope: broadest container we can reliably find.
      var host =
        this.closest('product-info') ||
        this.closest('.product') ||
        document.getElementById('MainProduct-' + this.sectionId) ||
        document.getElementById(this.productFormId);

      var searchRoot = host || document;

      var radio = null;

      // Primary: data-option-value-id (most precise)
      if (optionMeta) {
        var positionMap = this.optionValueIds && this.optionValueIds[optionMeta.position];
        if (positionMap) {
          var valueId = positionMap[targetValue];
          if (valueId != null) {
            var el = searchRoot.querySelector('[data-option-value-id="' + valueId + '"]');
            if (el && el.tagName === 'INPUT' && el.type === 'radio') radio = el;
          }
        }
      }

      // Fallback: search ALL radios in the scope by value string.
      // Filter by option name to avoid matching radios from other option groups.
      if (!radio) {
        var allRadios = searchRoot.querySelectorAll('input[type="radio"]');
        var optionName = optionMeta ? optionMeta.name : null;
        var targetNormalized = targetValue.toLowerCase().trim();
        for (var i = 0; i < allRadios.length; i++) {
          var r = allRadios[i];
          if (!r.value || r.value.toLowerCase().trim() !== targetNormalized) continue;
          // If we have the option name, prefer radios whose name attribute
          // contains it — this avoids collisions with other option groups.
          if (optionName && r.name && r.name.toLowerCase().indexOf(optionName.toLowerCase()) === -1) continue;
          radio = r;
          break;
        }
        // Broader fallback: any radio with the right value in this scope.
        if (!radio) {
          for (var j = 0; j < allRadios.length; j++) {
            if (allRadios[j].value && allRadios[j].value.toLowerCase().trim() === targetNormalized) { radio = allRadios[j]; break; }
          }
        }
      }

      if (radio && !radio.checked) {
        radio.checked = true;
      }

      // Also refresh JS-managed active classes.
      var vs = this.findVariantSelects();
      if (vs) this.refreshOptionVisualState(vs, targetVariant, currentVariant);
    }

    findVariantSelects() {
      // variant-selects and variant-radios are both used by Dawn-based themes.
      // variant-swatch-king is used by the third-party app.
      var tags = ['variant-selects', 'variant-radios', 'variant-swatch-king'];

      for (var t = 0; t < tags.length; t++) {
        var tag = tags[t];

        var byId = document.getElementById(tag + '-' + this.sectionId);
        if (byId) return byId;

        var bySectionAttr = document.querySelector(
          tag + '[data-section="' + this.sectionId + '"]'
        );
        if (bySectionAttr) return bySectionAttr;
      }

      var host = this.closest('product-info') || this.closest('.product__info-container') || this.closest('.product');
      if (host) {
        for (var t2 = 0; t2 < tags.length; t2++) {
          var withinHost = host.querySelector(tags[t2]);
          if (withinHost) return withinHost;
        }
        // Fallback: use the host itself as the root if no known wrapper is found
        return host;
      }

      var form = document.getElementById(this.productFormId);
      if (form) return form.closest('.product') || form;

      return document; // Ultimate fallback so switchVariant doesn't abort
    }

    setOptionControlValue(variantSelects, optionMeta, targetValue) {
      var byId = this.setOptionControlValueById(variantSelects, optionMeta, targetValue);
      if (byId) return byId;

      console.warn(
        '[framed-upgrade] No data-option-value-id match for',
        optionMeta.name, '=', targetValue,
        '— falling back to name/value selector matching.'
      );
      return this.setOptionControlValueByNameConvention(variantSelects, optionMeta, targetValue);
    }

    setOptionControlValueById(variantSelects, optionMeta, targetValue) {
      var positionMap = this.optionValueIds && this.optionValueIds[optionMeta.position];
      if (!positionMap) return null;

      var valueId = positionMap[targetValue];
      if (valueId == null) return null;

      var el = variantSelects.querySelector('[data-option-value-id="' + valueId + '"]');
      if (!el) return null;

      if (el.tagName === 'INPUT' && el.type === 'radio') {
        // Return the element — the actual click is deferred to clickOptionControl()
        // so we don't fire it twice when iterating multiple axes.
        return el;
      }

      if (el.tagName === 'OPTION') {
        var select = el.closest('select');
        if (!select) return null;
        select.value = el.value;
        return select;
      }

      return null;
    }

    setOptionControlValueByNameConvention(variantSelects, optionMeta, targetValue) {
      var name = optionMeta.name;
      var position = optionMeta.position;

      var radioSelectors = [
        'input[type="radio"][name="' + cssAttrEscape(name) + '"]',
        'input[type="radio"][name="' + cssAttrEscape(name) + '-' + position + '"]',
      ];

      for (var s = 0; s < radioSelectors.length; s++) {
        var radios = variantSelects.querySelectorAll(radioSelectors[s]);
        for (var i = 0; i < radios.length; i++) {
          if (radios[i].value === targetValue) {
            // Return the element — actual click fired by clickOptionControl().
            return radios[i];
          }
        }
      }

      var selectSelectors = [
        'select[name="' + cssAttrEscape(name) + '"]',
        'select[name="options[' + cssAttrEscape(name) + ']"]',
      ];

      for (var sSel = 0; sSel < selectSelectors.length; sSel++) {
        var select = variantSelects.querySelector(selectSelectors[sSel]);
        if (!select) continue;
        for (var j = 0; j < select.options.length; j++) {
          if (select.options[j].value === targetValue) {
            select.selectedIndex = j;
            return select;
          }
        }
      }

      // Ultimate fallback for Variant Swatch King and similar apps
      // that replace form controls with custom div buttons.
      var searchRoot = (variantSelects.closest)
        ? (variantSelects.closest('.product') || variantSelects.closest('product-info') || variantSelects)
        : document;
        
      var targetNormalized = targetValue.toLowerCase().trim();

      var customBtns = this._findSwatchAppButtons(searchRoot, targetNormalized);
      if (customBtns.length > 0) return customBtns[0]; // just return the first for control purposes

      // Failsafe: if the app mangled the input[name] attribute so the convention
      // match failed, just look for ANY radio in the product container that has
      // the exact matching value string.
      var allRadios = searchRoot.querySelectorAll('input[type="radio"]');
      for (var r = 0; r < allRadios.length; r++) {
        if (allRadios[r].value && allRadios[r].value.toLowerCase().trim() === targetNormalized) {
          return allRadios[r];
        }
      }

      return null;
    }

    /**
     * Helper to find third-party swatch app custom elements by value.
     * Returns an array of ALL matching elements, as some themes render duplicate
     * blocks for desktop/mobile or sticky headers.
     */
    _findSwatchAppButtons(container, targetNormalized) {
      var customSelectors = [
        '.star-set-btn',
        '.swatch__item',
        '.color-swatch',
        '.swatch-element',
        'variant-swatch-king li',
        '.swatch-image-wrapper'
      ];
      var swatchBtns = container.querySelectorAll(customSelectors.join(', '));
      var matches = [];
      
      for (var k = 0; k < swatchBtns.length; k++) {
        var sBtn = swatchBtns[k];
        var sVal = sBtn.getAttribute('data-value') || 
                   sBtn.getAttribute('value') || 
                   sBtn.getAttribute('aria-label') || 
                   sBtn.getAttribute('title');
        
        if (!sVal) {
          var titleEl = sBtn.querySelector('.swatch-button-title-text, .swatch-title, .tooltip-text');
          if (titleEl) sVal = titleEl.textContent;
          else sVal = sBtn.textContent;
        }
        
        if (sVal && sVal.toLowerCase().trim() === targetNormalized) {
          matches.push(sBtn);
        }
      }
      return matches;
    }

    /**
     * Fires the most faithful simulation of a real user interaction on a
     * form control:
     *
     *   Radio  → clicks the wrapping <label> (preferred) or the input itself.
     *            label.click() sets :checked, fires click + change on the
     *            radio, and updates all CSS pseudo-class styles — identical
     *            to the customer clicking the pill button.
     *
     *   Select → dispatches a bubbling 'change' event (selects don't need
     *            a click simulation; value was already set by the caller).
     *
     * We deliberately do NOT call .click() when the radio is already checked
     * to avoid a no-op that skips the 'change' event.
     */
    clickOptionControl(control) {
      if (!control) return;

      // If control is a custom Swatch app div
      if (control.tagName !== 'INPUT' && control.tagName !== 'SELECT') {
        control.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        return;
      }

      if (control.tagName === 'INPUT' && control.type === 'radio') {
        // LAYER 1 — Direct property: instantly updates CSS :checked pseudo-class
        // regardless of how the theme styles the pill. This is browser-native
        // and cannot be blocked by theme JS.
        control.checked = true;

        // LAYER 2 — Native event pipeline: fire click on the label (preferred)
        // so themes that listen for 'click' events on labels see a real user
        // interaction. Fall back to clicking the radio itself.
        var label =
          control.closest('label') ||
          (control.id
            ? document.querySelector('label[for="' + control.id.replace(/"/g, '\\"') + '"]')
            : null);

        if (label) {
          // label.click() when radio is already checked is a browser no-op for
          // the checked state, but still fires the click event on the label
          // which some themes use to trigger variant switching.
          label.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        }

        // Variant Swatch King custom click support
        var host = control.closest('.product') || control.closest('product-info') || document;
        var val = control.value;
        if (val) {
          var matchingBtns = this._findSwatchAppButtons(host, val.toLowerCase().trim());
          for (var b = 0; b < matchingBtns.length; b++) {
            var sBtn = matchingBtns[b];
            // Click the swatch app button directly to trigger its internal logic.
            // Dispatch to the innermost element in case listeners are bound there.
            var targetEl = sBtn.firstElementChild || sBtn;
            var opts = { bubbles: true, cancelable: true };
            targetEl.dispatchEvent(new MouseEvent('mousedown', opts));
            targetEl.dispatchEvent(new MouseEvent('mouseup', opts));
            targetEl.dispatchEvent(new MouseEvent('click', opts));
          }
        }

        // Always fire change on the radio — this is what variant-selects.js
        // listens to for updateMasterId / price / media / URL updates.
        control.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      // <select> — value was set by caller; just fire change.
      control.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // -------------------------------------------------------------------------
    // NEW: Visual active-state sync
    // -------------------------------------------------------------------------

    /**
     * Immediately refreshes the visual active/selected state on the theme's
     * variant option labels and buttons so the UI reflects the new selection
     * without waiting for the theme's async re-render cycle.
     *
     * Dawn and most Dawn-derived themes mark the selected pill/swatch with:
     *   - `aria-pressed="true"` on the <input type="radio">
     *   - A CSS class `.active` (or similar) on the surrounding <label> / <li>
     *
     * We sync both for every option axis that changed.
     */
    refreshOptionVisualState(variantSelects, targetVariant, currentVariant) {
      if (!variantSelects || !targetVariant || !currentVariant) return;

      for (var pos = 0; pos < targetVariant.options.length; pos++) {
        // Only touch axes that actually changed.
        if (currentVariant.options[pos] === targetVariant.options[pos]) continue;

        var optionMeta = this.options[pos];
        if (!optionMeta) continue;

        var positionMap = this.optionValueIds && this.optionValueIds[optionMeta.position];

        // ------------------------------------------------------------------
        // Collect ALL radio inputs that belong to this option position.
        // Primary: match by data-option-value-id (most reliable).
        // Fallback: match by input[name] convention.
        // ------------------------------------------------------------------
        var allRadios = [];

        if (positionMap) {
          var ids = Object.values(positionMap);
          ids.forEach(function (id) {
            var el = variantSelects.querySelector('[data-option-value-id="' + id + '"]');
            if (el && el.tagName === 'INPUT' && el.type === 'radio') {
              allRadios.push(el);
            }
          });
        }

        if (!allRadios.length) {
          // Fallback: gather by name convention.
          var nameSelectors = [
            'input[type="radio"][name="' + cssAttrEscape(optionMeta.name) + '"]',
            'input[type="radio"][name="' + cssAttrEscape(optionMeta.name) + '-' + optionMeta.position + '"]',
          ];
          nameSelectors.forEach(function (sel) {
            variantSelects.querySelectorAll(sel).forEach(function (r) {
              allRadios.push(r);
            });
          });
        }

        allRadios.forEach(function (radio) {
          var isSelected = radio.checked;

          // 1. aria-pressed — used by button-style radio patterns.
          radio.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

          // 2. Active class on the nearest meaningful ancestor element.
          //    Covers: <label>, <li>, [data-option-item], .swatch__item, etc.
          var ancestor =
            radio.closest('label') ||
            radio.closest('li') ||
            radio.closest('[data-option-item]') ||
            radio.closest('.swatch__item') ||
            radio.closest('.color-swatch');

          if (!ancestor) return;

          // Add/remove `.active` (Dawn default).
          ancestor.classList.toggle('active', isSelected);

          // Also handle themes that use `is-active` or `selected`.
          ancestor.classList.toggle('is-active', isSelected);
          ancestor.classList.toggle('selected', isSelected);

          // Some themes set aria-checked on the label itself.
          if (ancestor.hasAttribute('aria-checked')) {
            ancestor.setAttribute('aria-checked', isSelected ? 'true' : 'false');
          }

          // Pulse animation: briefly add a scoped class so CSS can flash the
          // newly selected pill — mirrors the "hover" feel of the active state
          // snapping into place. Remove after animation completes (600 ms).
          if (isSelected) {
            ancestor.classList.remove('framed-upgrade--just-selected');
            // Force a reflow so re-adding the class always re-triggers the animation
            // even if the customer clicks Upgrade more than once.
            void ancestor.offsetWidth;
            ancestor.classList.add('framed-upgrade--just-selected');
            window.setTimeout(function () {
              ancestor.classList.remove('framed-upgrade--just-selected');
            }, 600);
          }
        });

        // ------------------------------------------------------------------
        // <select> dropdown mirror — update a visible <span> that some
        // themes use to show the currently selected value.
        // ------------------------------------------------------------------
        var selectEl = variantSelects.querySelector(
          'select[name="' + cssAttrEscape(optionMeta.name) + '"], ' +
          'select[name="options[' + cssAttrEscape(optionMeta.name) + ']"]'
        );
        if (selectEl) {
          var mirror =
            selectEl.closest('[data-select-wrapper]') &&
            selectEl.closest('[data-select-wrapper]').querySelector('[data-selected-value]');
          if (mirror) {
            var chosen = selectEl.options[selectEl.selectedIndex];
            if (chosen) mirror.textContent = chosen.textContent;
          }
        }

        // ------------------------------------------------------------------
        // Variant Swatch King (third-party app) visual sync
        // ------------------------------------------------------------------
        var swatchKingHost = document.querySelector('variant-swatch-king') || variantSelects;
        
        var framedBtns = this._findSwatchAppButtons(swatchKingHost, targetVariant.options[pos].toLowerCase().trim());
        for (var fb = 0; fb < framedBtns.length; fb++) {
          var fBtn = framedBtns[fb];
          fBtn.classList.add('swatch-selected');
          fBtn.classList.remove('framed-upgrade--just-selected');
          void fBtn.offsetWidth;
          fBtn.classList.add('framed-upgrade--just-selected');
          (function (btn) {
            window.setTimeout(function () {
              btn.classList.remove('framed-upgrade--just-selected');
            }, 600);
          })(fBtn);
        }

        var unframedBtns = this._findSwatchAppButtons(swatchKingHost, currentVariant.options[pos].toLowerCase().trim());
        for (var ub = 0; ub < unframedBtns.length; ub++) {
          var uBtn = unframedBtns[ub];
          // Ensure we don't accidentally remove from a button we just added it to (shouldn't happen but defensive)
          if (framedBtns.indexOf(uBtn) === -1) {
            uBtn.classList.remove('swatch-selected');
          }
        }
      }
    }

    // -------------------------------------------------------------------------
    // Analytics
    // -------------------------------------------------------------------------

    fireAnalytics(currentVariant, framedVariant) {
  try {
    window.dataLayer = window.dataLayer || [];

    var productInfo = this.closest('product-info');

    var productTitle =
      productInfo?.querySelector('.product__title h1')?.textContent?.trim() ||
      productInfo?.querySelector('.product__title')?.textContent?.trim() ||
      document.title;

    var size =
      this.sizeAxisPosition != null ? currentVariant.options[this.sizeAxisPosition] : null;

    var oldPrice = Number(currentVariant.price || 0) / 100;
    var newPrice = Number(framedVariant.price || 0) / 100;
    var priceDifference = Number((newPrice - oldPrice).toFixed(2));

    var currency =
      this.currency ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      'GBP';

    var eventData = {
      send_to: 'G-2PG5X4032G',
      debug_mode: true,

      product_id: productInfo?.dataset.productId || '',
      product_title: productTitle,

      original_variant_id: String(currentVariant.id),
      original_variant_title: currentVariant.options.join(' / '),

      upgraded_variant_id: String(framedVariant.id),
      upgraded_variant_title: framedVariant.options.join(' / '),

      size: size,

      old_price: oldPrice,
      new_price: newPrice,
      price_difference: priceDifference,

      value: priceDifference,
      currency: currency,

      page_location: window.location.href,
      page_path: window.location.pathname
    };

    window.dataLayer.push({
      event: 'framed_upgrade_selected',
      ...eventData
    });

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'framed_upgrade_selected', eventData);
    }

    console.log('[framed-upgrade] GA4 event fired:', eventData);

  } catch (error) {
    console.error('[framed-upgrade] analytics error', error);
  }
}
  }

  function cssAttrEscape(value) {
    return String(value).replace(/(["\\])/g, '\\$1');
  }

  customElements.define('framed-upgrade-box', FramedUpgradeBoxElement);
})();
